import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { Card, Container, Row, Col, Button, Form, Badge, Spinner, Modal, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faIdCard, 
  faPhone, 
  faCar, 
  faMoneyBillWave, 
  faUserTie, 
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import { API_ENDPOINTS, API_METHODS } from '../services/constants';
import '../styles/Soforlerim.css';

// Araç sahibi ve şoför için arayüzler
interface AracSahibi {
  uyeid: number;
  uyeno: number;
  kimlikno: string;
  adi: string;
  soyadi: string;
  ceptel: string;
  grubu: string;
  plaka: string;
  borc: number;
  durum: string;
  kartresim: string;
}

interface Sofor {
  sofor_uyeid: number;
  UYENO: number;
  KIMLIKNO: string;
  ADI: string;
  SOYADI: string;
  CEPTEL: string;
  GRUBU: string;
  DURUMU: string;
  borc: number;
  kartresim: string;
}

const Soforlerim: React.FC = () => {
  const navigate = useNavigate();
  const [aracSahibi, setAracSahibi] = useState<AracSahibi | null>(null);
  const [soforler, setSoforler] = useState<Sofor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSoforId, setSelectedSoforId] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSofor, setNewSofor] = useState({
    kimlikno: '',
    adi: '',
    soyadi: '',
    ceptel: ''
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Native platform kontrolü
    if (Capacitor.isNativePlatform()) {
      console.log('Native platformda Soforlerim sayfası açılıyor...');
    }
    
    const userInfo = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');
    const isAuth = localStorage.getItem('isAuthenticated') === 'true';
    
    console.log('Soforlerim - Platform:', Capacitor.getPlatform());
    console.log('Soforlerim - UserInfo var mı:', !!userInfo);
    console.log('Soforlerim - Auth durumu:', isAuth);
    
    if (!userInfo || !isAuth) {
      console.log('Soforlerim - Auth yok, login\'e yönlendiriliyor');
      navigate('/login');
      return;
    }
    
    setIsAuthenticated(true);
    const user = JSON.parse(userInfo);
    console.log('Soforlerim - User data:', user);
    fetchData(user.uyeid, user);
  }, [navigate]);

  const fetchData = async (uyeId: string, userInfo: any) => {
    setLoading(true);
    setError(null);
    try {
      console.log('Soforlerim - fetchData başlıyor:', { uyeId, userInfo });
      
      const formData = new FormData();
      formData.append('method', API_METHODS.GET_ARAC_BILGILERI);
      formData.append('uyeid', uyeId);
      if (userInfo.plaka) formData.append('plaka', userInfo.plaka);
      if (userInfo.adi) formData.append('adi', userInfo.adi);
      if (userInfo.soyadi) formData.append('soyadi', userInfo.soyadi);
      
      console.log('Soforlerim - FormData parametreleri:');
      console.log('method:', API_METHODS.GET_ARAC_BILGILERI);
      console.log('uyeid:', uyeId);
      if (userInfo.plaka) console.log('plaka:', userInfo.plaka);
      if (userInfo.adi) console.log('adi:', userInfo.adi);
      if (userInfo.soyadi) console.log('soyadi:', userInfo.soyadi);
      console.log('Soforlerim - API Endpoint:', API_ENDPOINTS.WEBSERVICE);
      
      const response = await axios.post(API_ENDPOINTS.WEBSERVICE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      console.log('Soforlerim - Response status:', response.status);
      console.log('Soforlerim - Response data:', response.data);
      
      if (response.data.success) {
        console.log('Soforlerim - Araç sahibi:', response.data.arac_sahibi);
        console.log('Soforlerim - Şoförler:', response.data.soforler);
        setAracSahibi(response.data.arac_sahibi);
        setSoforler(response.data.soforler || []);
      } else {
        console.error('Soforlerim - API Hatası:', response.data.message);
        setError(response.data.message || 'Veri alınamadı');
      }
    } catch (err: any) {
      console.error('Soforlerim - Fetch error:', err);
      if (err.response) {
        console.error('Soforlerim - Error response:', err.response.data);
        console.error('Soforlerim - Error status:', err.response.status);
      }
      setError(err?.response?.data?.message || err?.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSoforSil = async () => {
    if (!selectedSoforId) return;
    
    try {
      setLoading(true);
      const response = await axios.post('https://ihlmobil.com/api/sofor_sil.php', {
        sofor_uyeid: selectedSoforId
      });

      if (response.data.success) {
        setSoforler(prevSoforler => prevSoforler.filter(sofor => sofor.sofor_uyeid !== selectedSoforId));
        setShowDeleteModal(false);
        setSelectedSoforId(null);
      } else {
        setError(response.data.message || 'Şoför silinirken bir hata oluştu');
      }
    } catch (err) {
      setError('Şoför silinirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSofor = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await axios.post('https://ihlmobil.com/api/sofor_ekle.php', {
        kimlikno: newSofor.kimlikno,
        adi: newSofor.adi,
        soyadi: newSofor.soyadi,
        ceptel: newSofor.ceptel
      });

      if (response.data.success) {
        setShowAddModal(false);
        setNewSofor({
          kimlikno: '',
          adi: '',
          soyadi: '',
          ceptel: ''
        });
        fetchData(sessionStorage.getItem('uyeId') || '', JSON.parse(localStorage.getItem('userInfo') || '{}'));
      } else {
        setError(response.data.message || 'Şoför eklenirken bir hata oluştu');
      }
    } catch (err) {
      setError('Şoför eklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };



  if (!isAuthenticated) {
    return null;
  }

  if (loading && !aracSahibi) {
    return (
      <div className="soforlerim-loading d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" variant="primary" style={{ width: 64, height: 64 }} />
        <div className="loading-text mt-3">Yükleniyor, lütfen bekleyiniz...</div>
      </div>
    );
  }

  return (
    <div className="soforlerim-container" style={{ paddingTop: 70 }}>
      <Header title="Şoförlerim" />
      <Container className="py-4">
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
        {/* Araç Sahibi Kartı */}
        {aracSahibi && (
          <Card className="owner-card mb-4 shadow-sm border-0 rounded-4 w-100" style={{ maxWidth: 480 }}>
            <Card.Header className="bg-primary text-white d-flex align-items-center rounded-top-4">
              <img src={aracSahibi.kartresim ? `https://crm.havalimanitaksi.com.tr/media/natural/${aracSahibi.kartresim}` : '/images/default-user.png'} alt={aracSahibi.adi} className="owner-avatar me-3" style={{ width: 64, height: 64, borderRadius: '50%' }} />
              <div>
                <h2 className="mb-1 fs-4 fw-bold">{aracSahibi.adi} {aracSahibi.soyadi}</h2>
                <div className="d-flex flex-wrap gap-2">
                  <Badge bg="light" text="dark"><FontAwesomeIcon icon={faUserTie} className="me-1" /> {aracSahibi.grubu}</Badge>
                  <Badge bg={aracSahibi.durum === 'AKTIF' ? 'success' : 'danger'}>{aracSahibi.durum}</Badge>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="bg-white rounded-bottom-4" style={{ color: '#222', fontFamily: 'Segoe UI, Arial, sans-serif' }}>
              <Row className="gy-3">
                <Col xs={12} md={6}><div className="info-block" style={{ color: '#222' }}><FontAwesomeIcon icon={faPhone} className="me-2 text-primary" /> <span className="fw-semibold">Telefon:</span> {aracSahibi.ceptel}</div></Col>
                <Col xs={12} md={6}><div className="info-block" style={{ color: '#222' }}><FontAwesomeIcon icon={faCar} className="me-2 text-primary" /> <span className="fw-semibold">Plaka:</span> {aracSahibi.plaka}</div></Col>
                <Col xs={12} md={6}><div className="info-block" style={{ color: '#222' }}><FontAwesomeIcon icon={faIdCard} className="me-2 text-primary" /> <span className="fw-semibold">Kimlik No:</span> {aracSahibi.kimlikno}</div></Col>
                <Col xs={12} md={6}><div className="info-block" style={{ color: '#222' }}><FontAwesomeIcon icon={faMoneyBillWave} className="me-2 text-primary" /> <span className="fw-semibold">Toplam Borç:</span> {aracSahibi.borc.toFixed(2)} ₺</div></Col>
              </Row>
            </Card.Body>
          </Card>
        )}
        {/* Şoförler Listesi */}
        <div className="drivers-section w-100 d-flex flex-column align-items-center" style={{ maxWidth: 480 }}>
          <div className="section-header d-flex align-items-center mb-3 w-100">
            <h3 className="section-title fs-5 fw-bold text-primary mb-0"><FontAwesomeIcon icon={faUserTie} className="me-2" />Şoförler</h3>
          </div>
          {soforler.length === 0 ? (
            <Card className="text-center p-4 empty-drivers border-0 shadow-sm rounded-4 w-100">
              <Card.Body>
                <div className="empty-icon mb-2"><FontAwesomeIcon icon={faUser} size="2x" /></div>
                <h4 className="mb-2">Henüz şoför eklenmemiş</h4>
                <p className="text-muted">Şoför kaydınız bulunmamaktadır.</p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="driver-cards g-3 w-100 justify-content-center">
              {soforler.map(sofor => (
                <Col key={sofor.sofor_uyeid} xs={12} className="mb-3 d-flex justify-content-center">
                  <Card className="driver-card h-100 border-0 shadow-sm rounded-4 w-100" style={{ maxWidth: 420 }}>
                    <Card.Body className="d-flex align-items-center gap-3">
                      <div className="driver-avatar flex-shrink-0">
                        <img src={sofor.kartresim ? `https://crm.havalimanitaksi.com.tr/media/natural/${sofor.kartresim}` : '/images/default-user.png'} alt={sofor.ADI} style={{ width: 56, height: 56, borderRadius: '50%' }} />
                      </div>
                      <div className="driver-details flex-grow-1">
                        <h4 className="driver-name mb-1 fs-6 fw-bold">{sofor.ADI} {sofor.SOYADI}</h4>
                        <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                          <Badge bg={sofor.DURUMU === 'AKTIF' ? 'success' : 'danger'}>{sofor.DURUMU}</Badge>
                          <span className="text-muted small">{sofor.CEPTEL}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <FontAwesomeIcon icon={faMoneyBillWave} className="text-primary" />
                          <span className="fw-semibold">Borç:</span>
                          <span className="fw-bold text-danger">{sofor.borc.toFixed(2)} ₺</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      </Container>
      
      <TabBar />
      
      {/* Şoför Silme Modali */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Şoför Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            <div className="warning-icon">
              <FontAwesomeIcon icon={faTrash} />
            </div>
            <h4>Şoförü silmek istediğinizden emin misiniz?</h4>
            <p className="text-muted">Bu işlem geri alınamaz.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleSoforSil} disabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Sil'}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Şoför Ekleme Modali */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Şoför Ekle</Modal.Title></Modal.Header>
        <Form onSubmit={handleAddSofor} className="p-3">
          <Form.Group className="mb-3">
            <Form.Label>Adı</Form.Label>
            <Form.Control type="text" value={newSofor.adi} onChange={e => setNewSofor({ ...newSofor, adi: e.target.value })} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Soyadı</Form.Label>
            <Form.Control type="text" value={newSofor.soyadi} onChange={e => setNewSofor({ ...newSofor, soyadi: e.target.value })} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Kimlik No</Form.Label>
            <Form.Control type="text" value={newSofor.kimlikno} onChange={e => setNewSofor({ ...newSofor, kimlikno: e.target.value })} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Cep Telefonu</Form.Label>
            <Form.Control type="text" value={newSofor.ceptel} onChange={e => setNewSofor({ ...newSofor, ceptel: e.target.value })} required />
          </Form.Group>
          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>İptal</Button>
            <Button variant="primary" type="submit" disabled={loading}>{loading ? <Spinner size="sm" /> : 'Ekle'}</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Soforlerim; 